import { observer } from "mobx-react-lite";
import { Button, Table, Tag } from "antd";
import { useCallback, useEffect, useState } from "react";
import { toast } from "bulma-toast";
import { Modal } from "../../../components";
import { useStore } from "../../../stores";
import { MdOutlineRefresh, MdClose } from "react-icons/md";
import { WorkflowEvent } from "../../../models/Workflow";

const ChatWorkflows = ({
  onClose,
  chatId,
}: {
  chatId: string;
  onClose: () => void;
}) => {
  const { selectedWorkspace, workspaceLoaded, functionLoaded } =
    useStore("workspaces");
  const { botSelected } = useStore("bots");
  const { 
    loadActiveWorkflowEvents, 
    cancelWorkflowEvent: cancelWorkflowEventStore 
  } = useStore("workflows");

  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [workflowData, setWorkflowData] = useState<any>(null);

  const loadActiveWorkflows = useCallback(async () => {
    if (!botSelected || !chatId) return;

    setLoading(true);

    try {
      const data = await loadActiveWorkflowEvents(chatId, "running", 1);
      setWorkflowData(data);
    } catch (error) {
      console.log(error);
      if (error instanceof Error) {
        toast({
          message: error.message,
          position: "top-right",
          dismissible: true,
          type: "is-danger",
          pauseOnHover: true,
        });
      }
    } finally {
      setLoading(false);
    }
  }, [botSelected, chatId, loadActiveWorkflowEvents]);

  const cancelWorkflow = useCallback(async (eventId: number) => {
    if (!botSelected) return;

    setActionLoading(`cancel-${eventId}`);

    try {
      await cancelWorkflowEventStore(eventId);
      
      toast({
        message: "Workflow cancelled successfully",
        position: "top-right",
        dismissible: true,
        type: "is-success",
        pauseOnHover: true,
      });

      // Reload the list
      loadActiveWorkflows();
    } catch (error) {
      console.log(error);
      if (error instanceof Error) {
        toast({
          message: error.message,
          position: "top-right",
          dismissible: true,
          type: "is-danger",
          pauseOnHover: true,
        });
      }
    } finally {
      setActionLoading(null);
    }
  }, [botSelected, cancelWorkflowEventStore, loadActiveWorkflows]);

  useEffect(() => {
    if (botSelected && chatId) {
      loadActiveWorkflows();
    }
  }, [botSelected, chatId, loadActiveWorkflows]);

  const getStatusColor = (status: string | null) => {
    if (!status) return "processing"; // pending
    switch (status) {
      case "in-progress":
        return "blue";
      case "completed":
        return "green";
      case "failed":
        return "red";
      case "cancelled":
        return "orange";
      default:
        return "default";
    }
  };

  const getStatusText = (status: string | null) => {
    if (!status) return "Pending";
    switch (status) {
      case "in-progress":
        return "Running";
      case "completed":
        return "Completed";
      case "failed":
        return "Failed";
      case "cancelled":
        return "Cancelled";
      default:
        return status;
    }
  };

  const columns = [
    {
      title: "Workflow",
      key: "workflow_name",
      className: "has-text-grey",
      width: 200,
      render: (_: any, item: WorkflowEvent) => {
        // Extract workflow name from workflowData if available
        const workflowName = item.workflowData?.name || `Workflow ${item.id}`;
        return workflowName;
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      className: "has-text-grey",
      width: 120,
      render: (status: string | null) => (
        <Tag color={getStatusColor(status)}>
          {getStatusText(status)}
        </Tag>
      ),
    },
    {
      title: "Progress",
      key: "progress",
      className: "has-text-grey",
      width: 100,
      render: (_: any, item: WorkflowEvent) => {
        const completedNodes = item.meta?.completedNodes?.length || 0;
        return `${completedNodes} nodes`;
      },
    },
    {
      title: "Started",
      dataIndex: "createdAt",
      key: "created_at",
      className: "has-text-grey",
      width: 150,
      render: (createdAt: any) => {
        return createdAt?.toFormat("M/d/yyyy, h:mm a") || "N/A";
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 100,
      render: (_: any, item: WorkflowEvent) => {
        const isActive = !item.status || item.status === "in-progress";
        const isLoading = actionLoading === `cancel-${item.id}`;
        
        if (!isActive) return null;

        return (
          <Button
            type="text"
            danger
            size="small"
            icon={<MdClose size={16} />}
            loading={isLoading}
            onClick={() => cancelWorkflow(item.id!)}
            title="Cancel Workflow"
          />
        );
      },
    },
  ];

  if (!selectedWorkspace || !workspaceLoaded || !functionLoaded || !botSelected)
    return null;

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={() => (
        <div className="is-flex is-align-items-center">
          <h2 className="title is-4 mb-2 mr-3">Chat Workflows</h2>
          <Button
            type="text"
            className="ml-3"
            onClick={loadActiveWorkflows}
            disabled={loading}
            ghost
            size="small"
            icon={<MdOutlineRefresh size={18} />}
          />
        </div>
      )}
      isLargeScreen
    >
      <div className="mb-4">
        <p className="has-text-grey">
          Active workflows running for this conversation
        </p>
      </div>
      
      <Table
        dataSource={workflowData}
        columns={columns}
        loading={loading}
        size="small"
        rowKey="id"
        locale={{
          emptyText: "No active workflows found for this chat",
        }}
        scroll={{
          x: 600,
        }}
      />
    </Modal>
  );
};

export default observer(ChatWorkflows);